import { useState } from "react";
import { Button } from "@component/button";
import { Card, CardContent } from "@component/card";
import { EmployerDomainVerification } from "@type/auth";
import { ArrowRightIcon, CheckCircleIcon } from "lucide-react";
import { Label } from "@component/label";
import { Textarea } from "@component/textarea";
import { requestJoinTeam } from "@service/auth";

interface Props {
    userEmail?: string;
    domainVerification: EmployerDomainVerification | undefined
}

export default function RequestJoinTeam(props: Props) {
    const [showNoteSection, setShowNoteSection] = useState<boolean>(false);
    const [note, setNote] = useState<string>("");
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isResending, setIsResending] = useState<boolean>(false);
    const [resendSuccess, setResendSuccess] = useState<boolean>(false);
    const [requested, setRequested] = useState<boolean>(false);
    const maxNoteLength = 100;

    const handleRequestToJoin = () => {
        setShowNoteSection(true);
    };

    const handleBack = () => {
        setShowNoteSection(false);
        setNote("");
    };

    const handleSendRequest = () => {
        setIsLoading(true);
        requestJoinTeam({
            email: props.userEmail || "",
            company_id: props.domainVerification?.company_id || "",
            notes: note,
        })
            .then((resp) => {
                if (resp.status === 200) {
                    setRequested(true);
                }
            })
            .catch(() => {
                setIsLoading(false);
            })
    }

    const handleResendEmail = () => {
        setIsResending(true);
        requestJoinTeam({
            email: props.userEmail || "",
            company_id: props.domainVerification?.company_id || "",
            notes: note,
        })
            .then(() => {
                setIsResending(false);
                setResendSuccess(true);
            })
            .catch(() => {
                setIsResending(false);
            });
    }

    return (
        <div className="flex items-center justify-center min-h-full">
            {!requested ? (
                <Card className="w-full max-w-xl">
                    <CardContent className="space-y-5 px-6 sm:px-8">
                        {/* Team Found Section */}
                        <div
                            className="text-center space-y-4"
                            style={{ paddingTop: "24px", paddingBottom: "0px" }}
                        >
                            <div>
                                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                                    Request to join {props.domainVerification?.company_name}'s workspace
                                </h3>
                                <p className="text-sm text-gray-600">
                                    Your email matches a company domain that already has a
                                    workspace.
                                    <br />
                                    For data security, the workspace owner must approve your access.
                                </p>
                            </div>
                        </div>

                        {/* Team Members Section */}
                        <div className="space-y-3">
                            <div className="relative overflow-hidden rounded-xl border border-blue-200 bg-gradient-to-br from-blue-50 via-white to-blue-50/30">
                                {/* Decorative background elements */}
                                <div className="absolute top-0 right-0 w-32 h-32 bg-blue-100/40 rounded-full blur-3xl" />
                                <div className="absolute bottom-0 left-0 w-24 h-24 bg-blue-200/30 rounded-full blur-2xl" />
                                <div className="relative p-6">
                                    <div className="flex flex-col items-center space-y-4 text-center">
                                        {/* Team Logo with enhanced styling */}
                                        <div className="relative flex-shrink-0">
                                            <div className="absolute inset-0 bg-blue-400/20 rounded-full blur-md" />
                                            <img
                                                src={`${import.meta.env.VITE_ASSET_OURTEAM_BASE_URL}/${props.domainVerification?.company_logo}`}
                                                onError={(e) => {
                                                    e.currentTarget.src = `${import.meta.env.VITE_ASSET_OURTEAM_BASE_URL}/commons/default_avatar.png`;
                                                }}
                                                className="relative w-16 h-16 rounded-full object-cover ring-2 ring-blue-200 ring-offset-2"
                                            />
                                        </div>

                                        {/* Team Details */}
                                        <div className="flex-1 min-w-0">
                                            <h3 className="text-lg font-bold text-gray-900 mb-1">
                                                {props.domainVerification?.company_name}
                                            </h3>
                                            <p className="text-sm text-gray-600 font-medium">
                                                {props.domainVerification?.domain_member_count} member
                                                {props.domainVerification?.domain_member_count !== 1 ? "s" : ""}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Join Request Section */}
                        <div className="space-y-4">
                            {!showNoteSection ? (
                                <>
                                    <div className="text-center">
                                        <Button
                                            onClick={handleRequestToJoin}
                                            className="w-full bg-blue-600 hover:bg-blue-700"
                                        >
                                            Request to Join
                                            <ArrowRightIcon className="ml-2 h-4 w-4" />
                                        </Button>
                                    </div>

                                    {/* Agreement Text */}
                                    <div className="text-center">
                                        <p className="text-xs text-gray-500">
                                            By proceeding, you agree to the Terms of Service at ourteam.
                                        </p>
                                    </div>
                                </>
                            ) : (
                                <div className="space-y-4">
                                    {/* Note Input */}
                                    <div className="space-y-2">
                                        <Label
                                            htmlFor="note-textarea"
                                            className="text-sm font-medium text-foreground"
                                        >
                                            Add a note to your request
                                        </Label>
                                        <div className="relative">
                                            <Textarea
                                                id="note-textarea"
                                                placeholder="Optional"
                                                value={note}
                                                onChange={(e) => {
                                                    if (e.target.value.length <= maxNoteLength) {
                                                        setNote(e.target.value);
                                                    }
                                                }}
                                                className="min-h-[120px] resize-none bg-gray-50 dark:bg-gray-950/20 border-gray-200 dark:border-gray-800 focus:border-gray-400 focus:ring-gray-400 pb-8"
                                            />

                                            <div className="absolute bottom-3 right-3 text-xs text-muted-foreground">
                                                {note.length} / {maxNoteLength}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Action Buttons */}
                                    <div className="flex items-center justify-end gap-3 pt-2">
                                        <Button
                                            variant="outline"
                                            onClick={handleBack}
                                            disabled={isLoading}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            onClick={handleSendRequest}
                                            disabled={isLoading}
                                            className="bg-blue-600 hover:bg-blue-700"
                                        >
                                            {isLoading ? "Sending..." : "Send request"}
                                        </Button>
                                    </div>

                                    {/* Agreement Text */}
                                    <div className="text-center">
                                        <p className="text-xs text-gray-500">
                                            By proceeding, you agree to the Terms of Service at ourteam.
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </CardContent>
                </Card>
            ) : (
                <Card className="w-full max-w-xl">
                    <CardContent className="space-y-5 px-8 sm:px-12">
                        <div
                            className="text-center space-y-4"
                            style={{ paddingTop: "24px", paddingBottom: "0px" }}
                        >
                            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                                <CheckCircleIcon className="w-8 h-8 text-green-600" />
                            </div>
                            <div>
                                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                                    Request Sent!
                                </h3>
                                <p className="text-sm text-gray-600">
                                    The workspace owner has been notified of your
                                    <br />
                                    request to join the workspace.
                                    <br />
                                    <br />
                                    You will receive an email once your request is approved.
                                </p>
                            </div>
                            <div className="pt-2 space-y-3">
                                <Button
                                    variant="link"
                                    onClick={handleResendEmail}
                                    disabled={isResending}
                                    className="text-blue-600 hover:text-blue-700 p-0 h-auto font-normal"
                                >
                                    {isResending
                                        ? "Sending..."
                                        : resendSuccess
                                            ? "Email sent!"
                                            : "Didn't receive an email? Send it again."}
                                </Button>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            )}
        </div>
    );
}
